import { fetchWithSupabaseAuth } from "./supabaseClient.js";

export const DRIVER_ACCOUNT_STATUS_LABELS = {
  active: "Actif", suspended: "Suspendu", no_account: "Aucun compte",
  legacy: "Hérité à rattacher", unavailable: "État indisponible",
};

export function resolveDriverSelectOptionParts(person = {}) {
  return {
    name: person.employee_name || "",
    assignedTruckLabel: person.assigned_truck_id
      ? String(person.assigned_truck_name || person.assigned_truck_id)
      : "",
  };
}

export function formatDriverSelectOptionLabel(person, { includeAccountDetails = false } = {}) {
  const { name, assignedTruckLabel } = resolveDriverSelectOptionParts(person);
  const truck = assignedTruckLabel ? ` · Camion ${assignedTruckLabel}` : "";
  if (!includeAccountDetails) return name;
  const email = person?.courriel || "Sans courriel";
  const status = DRIVER_ACCOUNT_STATUS_LABELS[person?.account_status] || "État indisponible";
  return `${name} — ${email} · ${status}${truck}`;
}

export function driverAssignmentChallenge(payload, status) {
  const detail = payload?.detail || payload;
  return status === 409 && detail?.code === "driver_assignment_confirmation_required"
    && detail?.versions && detail?.source_truck_id && detail?.target_truck_id ? detail : null;
}

const DISPATCH_DATE_FORMATTER = new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "long", timeZone: "UTC" });

export function driverTransferConfirmation(challenge) {
  const source = challenge.source_truck_name || challenge.source_truck_id;
  const target = challenge.target_truck_name || challenge.target_truck_id;
  return {
    title: "Transférer le chauffeur ?",
    cornerRadius: "grain", contentDensity: "compact", summaryDensity: "compact", tone: "accent",
    description: `${challenge.driver_name} quittera le camion ${source} pour le camion ${target}. Le ${source} passera à « Aucun chauffeur ».`,
    summaryItems: [
      { label: "Source", value: source },
      { label: "Destination", value: target },
      { label: "Chauffeur remplacé", value: challenge.replaced_driver_name || "Aucun chauffeur" },
      { label: "Portée", value: `${challenge.dispatch_date ? DISPATCH_DATE_FORMATTER.format(new Date(challenge.dispatch_date)) : "Par défaut"} — ${challenge.dispatch_period === "evening" ? "Soir" : "Jour"}` },
    ],
    confirmLabel: "Confirmer le transfert",
  };
}

export function confirmedDriverTransfer(body, challenge) {
  return { ...body, allow_transfer: true, confirmed_versions: { ...challenge.versions } };
}

// Tournée commencée (départ enregistré au portail) : le changement de chauffeur du jour se confirme.
export function driverTourStartedChallenge(payload, status) {
  const detail = payload?.detail || payload;
  return status === 409 && detail?.code === "driver_tour_started_confirmation_required" ? detail : null;
}

export function startedTourDeparturesLabel(count) {
  return `${count} départ${count > 1 ? "s" : ""} enregistré${count > 1 ? "s" : ""} au portail`;
}

// Restauration d'une sauvegarde qui change le chauffeur ou le camion de tournées commencées.
export function startedTourRestoreChallenge(payload, status) {
  const detail = payload?.detail;
  return status === 409 && detail?.code === "dispatch_snapshot_started_tour_confirmation_required"
    && Array.isArray(detail.started_tours) && detail.started_tours.length ? detail : null;
}

export function startedTourRestoreConfirmation(challenge) {
  return {
    title: "Tournées commencées",
    cornerRadius: "grain", contentDensity: "compact", summaryDensity: "compact", tone: "accent",
    description: "La sauvegarde change le chauffeur ou le camion de ces tournées commencées. Les départs déjà faits restent au chauffeur précédent.",
    summaryItems: challenge.started_tours.map((tour) => ({
      label: tour.trailer_label || tour.truck_id, value: startedTourDeparturesLabel(tour.departure_count),
    })),
    confirmLabel: "Restaurer quand même",
  };
}

export function driverTourStartedConfirmation(challenge) {
  const trailer = challenge.trailer_label || challenge.truck_id;
  return {
    title: "Tournée commencée",
    cornerRadius: "grain", contentDensity: "compact", summaryDensity: "compact", tone: "accent",
    description: `La tournée de ${trailer} est commencée (${startedTourDeparturesLabel(challenge.departure_count)}). Les départs déjà faits restent au chauffeur précédent ; le nouveau chauffeur reprend la suite.`,
    summaryItems: [
      { label: "Chauffeur actuel", value: challenge.replaced_driver_name || "Aucun chauffeur" },
      { label: "Nouveau chauffeur", value: challenge.driver_name || "Aucun chauffeur" },
      { label: "Portée", value: `${challenge.dispatch_date ? DISPATCH_DATE_FORMATTER.format(new Date(challenge.dispatch_date)) : ""} — ${challenge.dispatch_period === "evening" ? "Soir" : "Jour"}` },
    ],
    confirmLabel: "Changer quand même",
  };
}

export async function loadDriverCatalog({ dispatchDate = "", dispatchPeriod = "day", scope = "dispatch", signal } = {}) {
  const query = new URLSearchParams({ scope, dispatch_period: dispatchPeriod });
  if (dispatchDate) query.set("dispatch_date", dispatchDate);
  const response = await fetchWithSupabaseAuth(`/api/planning/drivers?${query}`, { signal });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload?.detail?.message || payload?.detail || "Catalogue des chauffeurs indisponible.");
  return payload;
}

export async function saveDefaultDriver(truckId, body) {
  const response = await fetchWithSupabaseAuth(`/api/data-manager/trucks/${encodeURIComponent(truckId)}/driver-assignment`, {
    method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const payload = await response.json();
  if (!response.ok) {
    const error = new Error(payload?.detail?.message || payload?.detail || "Impossible d'enregistrer le chauffeur.");
    error.challenge = driverAssignmentChallenge(payload, response.status);
    throw error;
  }
  return payload;
}
